import React from "react";

// Arrowheads are annotation, not geometry — sized in constant screen pixels
// (like strokeWidth) rather than scaled with `scale`/zoom, so they stay
// legible without ballooning at high zoom.
const ARROWHEAD_LENGTH = 16;
const ARROWHEAD_HALF_WIDTH = 7;

function arrowheadPoints(tipX, tipY, dx, dy) {
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const ux = dx / len;
  const uy = dy / len;
  const baseX = tipX - ux * ARROWHEAD_LENGTH;
  const baseY = tipY - uy * ARROWHEAD_LENGTH;
  const perpX = -uy * ARROWHEAD_HALF_WIDTH;
  const perpY = ux * ARROWHEAD_HALF_WIDTH;
  return `${tipX},${tipY} ${baseX + perpX},${baseY + perpY} ${baseX - perpX},${baseY - perpY}`;
}

const Connection = ({
  connection,
  source,
  target,
  // Set when this is a 'labels' edge targeting a Connection (naming a pipe)
  // instead of an Element (Phase 1.5) — a Connection has no boundingBox
  // center, so the caller (DrawingView.js) precomputes a point on the
  // target pipe's own path instead. Unscaled image coordinates, like
  // boundingBox — this component applies `scale` uniformly below.
  targetPoint,
  // "Show unlinked" clean-up: this connection is missing an end (graphCompleteness.js)
  isUnlinked,
  isHighlighted,
  onClick,             // (e, connection) — shared, stable handlers (see DrawingView)
  scale = 1,
  onEndpointMouseDown,
  onVertexMouseDown,
}) => {
  const pathSegments = connection.metadata?.path_data?.path;
  const hasPathData = Array.isArray(pathSegments) && pathSegments.length > 0;
  const isOrphan = connection.metadata?.is_orphan === true;

  // Orphans render from path data only — no source/target elements needed
  if (isOrphan && !hasPathData) return null;
  // Regular connections need valid endpoints if there's no path data
  if (!isOrphan && !source?.boundingBox && !hasPathData) return null;
  if (!isOrphan && !hasPathData && !target?.boundingBox && !targetPoint) return null;

  // Confirmation and colour
  const isConfirmed = isOrphan
    ? connection.confirmed
    : connection.confirmed && !!source?.confirmed && !!target?.confirmed;
  // Rejected is a soft-delete state (§0.2 item 6) — it wins over the
  // confirmed/orphan/unconfirmed coloring below, but a highlighted (selected)
  // connection still needs to read as selected so it can be found and
  // un-rejected.
  const isRejected = connection.metadata?.rejected === true;

  let strokeColor;
  if (isHighlighted && isOrphan && !isConfirmed) {
    strokeColor = "#e65100"; // deep orange — selected unconfirmed orphan
  } else if (isHighlighted) {
    strokeColor = "#1976d2"; // blue — selected element or confirmed connection
  } else if (isRejected) {
    strokeColor = "#f4716a"; // red — rejected (soft-deleted)
  } else if (isConfirmed) {
    strokeColor = "#4caf50"; // green — confirmed
  } else if (isOrphan) {
    strokeColor = "#ff8f00"; // amber — unconfirmed orphan
  } else {
    strokeColor = "#ff0000"; // red — unconfirmed regular connection
  }
  if (isUnlinked && !isHighlighted && !isRejected) strokeColor = "#38bdf8"; // sky — missing an end

  const isControlsLine = connection.relationshipType === "controls";
  // A `names`/`describes` edge (or legacy `labels`) attaches a text_label to
  // whatever it names/describes — it's an annotation link, not piping or
  // signal wiring, so it gets neither an arrowhead (below) nor the pipe/wire
  // dash patterns, just a fine dotted line to read as "association" not "flow".
  const isLabelLine = ["labels", "names", "describes"].includes(connection.relationshipType);
  // Directionality is only meaningful for material flow — control-system lines
  // don't get arrowheads (not good P&ID practice to imply direction on them).
  const isFlowLine = connection.relationshipType === "flows to";
  const isUnconfirmedRegular = !isOrphan && (!source?.confirmed || !target?.confirmed);
  const strokeDashPattern = isRejected && !isHighlighted
    ? "3,3"
    : isOrphan && !isConfirmed
    ? "6,4"
    : isLabelLine
    ? "2,3"
    : isControlsLine
    ? "10,6"
    : isUnconfirmedRegular
    ? "5,5"
    : "";
  const strokeOpacityValue = isRejected && !isHighlighted ? 0.55 : 1;

  const hitAreaWidth = 12;
  const svgStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    overflow: "visible",
  };

  if (hasPathData) {
    const lastSeg = pathSegments[pathSegments.length - 1];
    const arrowTipX = lastSeg.end[0] * scale;
    const arrowTipY = lastSeg.end[1] * scale;
    const arrowPoints = isFlowLine
      ? arrowheadPoints(
          arrowTipX,
          arrowTipY,
          lastSeg.end[0] - lastSeg.start[0],
          lastSeg.end[1] - lastSeg.start[1]
        )
      : null;

    return (
      <div>
        <svg width="100vw" height="100vh" style={svgStyle}>
          {pathSegments.map((seg, idx) => (
            <line
              key={idx}
              x1={seg.start[0] * scale}
              y1={seg.start[1] * scale}
              x2={seg.end[0] * scale}
              y2={seg.end[1] * scale}
              stroke={strokeColor}
              strokeWidth={isOrphan ? 4 : 4}
              strokeDasharray={strokeDashPattern}
              strokeOpacity={strokeOpacityValue}
            />
          ))}
          {arrowPoints && <polygon points={arrowPoints} fill={strokeColor} />}
          {pathSegments.map((seg, idx) => (
            <line
              key={`hit-${idx}`}
              data-hit-type="connection"
              data-hit-id={connection._id}
              x1={seg.start[0] * scale}
              y1={seg.start[1] * scale}
              x2={seg.end[0] * scale}
              y2={seg.end[1] * scale}
              stroke="transparent"
              strokeWidth={hitAreaWidth}
              style={{ cursor: "pointer", pointerEvents: "all" }}
              onClick={onClick ? (e) => onClick(e, connection) : undefined}
            />
          ))}
          {isHighlighted && onEndpointMouseDown && (<>
            <circle
              cx={pathSegments[0].start[0] * scale}
              cy={pathSegments[0].start[1] * scale}
              r={8} fill="#2dd4bf" stroke="#fff" strokeWidth={2}
              style={{ cursor: 'crosshair', pointerEvents: 'all' }}
              onMouseDown={(e) => { e.stopPropagation(); onEndpointMouseDown('source', e, connection); }}
            />
            <circle
              cx={pathSegments[pathSegments.length - 1].end[0] * scale}
              cy={pathSegments[pathSegments.length - 1].end[1] * scale}
              r={8} fill="#2dd4bf" stroke="#fff" strokeWidth={2}
              style={{ cursor: 'crosshair', pointerEvents: 'all' }}
              onMouseDown={(e) => { e.stopPropagation(); onEndpointMouseDown('target', e, connection); }}
            />
            {pathSegments.length >= 3 && pathSegments.slice(0, -2).map((seg, i) => (
              <rect key={`v-${i}`}
                x={seg.end[0] * scale - 5}
                y={seg.end[1] * scale - 5}
                width={10} height={10}
                fill="#2dd4bf" stroke="#fff" strokeWidth={2}
                style={{ cursor: 'move', pointerEvents: 'all' }}
                onMouseDown={(e) => { e.stopPropagation(); onVertexMouseDown && onVertexMouseDown(i, e, connection); }}
              />
            ))}
          </>)}
        </svg>
      </div>
    );
  }

  // Fallback: straight line (regular connections without path data) — from
  // the source element's center to either the target element's center, or
  // (for a labels edge naming a pipe) the precomputed nearest point on it.
  const sourceBox = source.boundingBox;
  const x1 = sourceBox.x * scale + (sourceBox.width * scale) / 2;
  const y1 = sourceBox.y * scale + (sourceBox.height * scale) / 2;
  const x2 = target?.boundingBox
    ? target.boundingBox.x * scale + (target.boundingBox.width * scale) / 2
    : targetPoint.x * scale;
  const y2 = target?.boundingBox
    ? target.boundingBox.y * scale + (target.boundingBox.height * scale) / 2
    : targetPoint.y * scale;
  const arrowPoints = isFlowLine ? arrowheadPoints(x2, y2, x2 - x1, y2 - y1) : null;

  return (
    <div>
      <svg width="100vw" height="100vh" style={svgStyle}>
        <line x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={strokeColor} strokeWidth={4}
          strokeDasharray={strokeDashPattern} strokeOpacity={strokeOpacityValue} />
        {arrowPoints && <polygon points={arrowPoints} fill={strokeColor} />}
        <line
          data-hit-type="connection"
          data-hit-id={connection._id}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="transparent" strokeWidth={hitAreaWidth}
          style={{ cursor: "pointer", pointerEvents: "all" }}
          onClick={onClick ? (e) => onClick(e, connection) : undefined} />
      </svg>
    </div>
  );
};

export default React.memo(Connection);
